function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES, useGetAllPaginatedData } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext, useContextWithValidation } from '@adsk/alloy-react-context';
import { useAlloyFeatureFlag } from './AlloyLaunchDarklyProvider';
import { PROJECT_CLASSIFICATION_TYPES } from './consts';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import { fetchWithAccountType } from './helpers/fetchWithAccountType';
import isProjectTemplate from './helpers/isProjectTemplate';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useLocalStateData from './hooks/useLocalStateData';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
/**
 * Context for project entitlements.
 */
export const ProjectEntitlementsContext = getOrCreateGlobalContext(CONTEXT_NAMES.ProjectEntitlementsContext);
export const useProjectEntitlements = () => useContextWithValidation(ProjectEntitlementsContext, {
  hook: 'useProjectEntitlements',
  provider: 'ProjectEntitlementsProvider'
});

/**
 * Hook for getting the current project the user work in.
 * This hook returns the current project, as returned from [project-entitlements](https://platform.stoplight.autodesk.com/docs/hq-api-server/swagger.yaml/paths/~1project-entitlements/get)
 request.
 * Return undefined if project entitlements are missing.
 * @returns {undefined|EntitlementsProject} The project entitlements or undefined
 * @example
 * import { useCurrentProject } from '@adsk/alloy-provider';
 * ...
 * const currentProject = useCurrentProject();
 */
export const useCurrentProject = () => {
  const {
    currentProject
  } = useProjectEntitlements();
  return currentProject;
};

/**
 * Hook for getting the current logged in user.
 * This hook returns the current user, as returned from [project-entitlements](https://platform.stoplight.autodesk.com/docs/hq-api-server/swagger.yaml/paths/~1project-entitlements/get)
 request.
 * @returns {{ user: EntitlementsUser, setUser: (user: EntitlementsUser) => void }} An object of shape {user, setUser} - containing the current user and setUser function to update the current user.
 * @example
 * import { useUser } from '@adsk/alloy-provider';
 * ...
 * const { user, setUser } = useUser();
 */
export const useUser = () => {
  const {
    user,
    setUser
  } = useProjectEntitlements();
  return useMemo(() => user ? {
    user,
    setUser
  } : {}, [user, setUser]);
};

/**
 * This hook returns whether or not the current project is a template.
 * @returns {boolean} Whether the current project entitlements is a template or not.
 * @example
 * import { useCurrentProjectIsTemplate } from '@adsk/alloy-provider';
 * ...
 * const isTemplate = useCurrentProjectIsTemplate();
 */
export const useCurrentProjectIsTemplate = () => {
  const currentProject = useCurrentProject();
  return currentProject ? isProjectTemplate(currentProject) : false;
};
const fields = ['accountId', 'name', 'accountName', 'accountImageUrl', 'defaultUrl', 'classification', 'imageUrl', 'region', 'products', 'platform', 'mfaBlocked'];
const classificationFilter = `filter[classification]=${Object.values(PROJECT_CLASSIFICATION_TYPES).join(',')}`;
const getCurrentProjectIdPart = projectId => projectId ? `currentProjectId=${projectId}&` : '';
const noNextPageGetNextKey = () => '';
/**
 * @summary Considerations for Feature Flag Usage within ProjectEntitlementsProvider
 * @description When working with feature flags within providers, follow these guidelines to prevent unexpected issues.
 *
 * @instructions
 * **Context Data Dependencies:**
 *  - Before modifying feature flags within a provider, assess dependencies on context data.
 *  - When feature flag is using targeting rules, it may be dependent on context data, context data isn't available until project-entitlements are fetched.
 *  - Understand potential impacts on provider behavior, especially in interaction with other components.
 */
export const ProjectEntitlementsProvider = _ref => {
  let {
    disableAllProjectsFetch,
    children
  } = _ref;
  const projectId = useCurrentProjectId();
  const [user, setUser] = useState(undefined);
  const [currentProject, setCurrentProject] = useState(undefined);
  const [url, setUrl] = useState('');
  const {
    value: slashClients,
    loading: slashClientsLoading
  } = useAlloyFeatureFlag('slash-clients');
  const {
    value: backendSearch,
    loading: backendSearchLoading
  } = useAlloyFeatureFlag('entitlements-backend-search', disableAllProjectsFetch);
  const disableAllPagesFetch = useMemo(() => disableAllProjectsFetch || backendSearch || slashClients, [disableAllProjectsFetch, backendSearch, slashClients]);
  const isFFLoading = useMemo(() => backendSearchLoading || slashClientsLoading, [backendSearchLoading, slashClientsLoading]);
  useProviderOnlyOnce({
    context: ProjectEntitlementsContext,
    contextName: CONTEXT_NAMES.ProjectEntitlementsContext
  });
  useEffect(() => {
    if (url || isFFLoading) {
      return;
    }
    const path = slashClients ? 'ui/v1/clients' : `v2/project-entitlements`;
    setUrl(`${getAdminApiUrl()}/${path}?${getCurrentProjectIdPart(projectId)}fields=${fields}&${classificationFilter}&limit=${disableAllPagesFetch ? 1 : 200}`);
  }, [backendSearchLoading, projectId, url, disableAllPagesFetch, slashClientsLoading, slashClients, isFFLoading]);
  const {
    data: dataFromBE,
    loading: loadingData,
    error,
    retry,
    clearSessionStorage
  } = useGetAllPaginatedData({
    url,
    getNextKey: isFFLoading || disableAllPagesFetch ? noNextPageGetNextKey : undefined,
    fetcher: fetchWithAccountType
  });
  useEffect(() => {
    if (error && error.status !== 401) {
      // 401 errors are pretty common and expected, here we want to mostly log unexpected ones
      console.error('Error calling project entitlements', error);
    }
  }, [error]);
  const firstPage = dataFromBE[0];
  useEffect(() => {
    if (firstPage) {
      if (firstPage.currentProject) {
        setCurrentProject(firstPage.currentProject);
      }
      if (firstPage.user) {
        setUser(firstPage.user);
      }
    }
  }, [firstPage]);
  const projectsFromBE = useMemo(() => {
    return dataFromBE && removeOldDuplicatesBy(dataFromBE.flatMap(page => page.results), item => item.id);
  }, [dataFromBE]);
  const {
    data: projects,
    addData: addProjects,
    updateEntity: updateProject,
    removeEntityById: removeProjectById
  } = useLocalStateData(projectsFromBE);
  const addProject = useCallback(project => {
    addProjects([project]);
  }, [addProjects]);
  const loading = !url || loadingData || Boolean(projectsFromBE.length && !projects.length);
  const setCurrentProjectAndClearCache = useCallback(project => {
    setCurrentProject(project);
    clearSessionStorage();
  }, [setCurrentProject, clearSessionStorage]);
  const value = useMemo(() => _objectSpread({
    loading,
    error,
    retry,
    projects,
    user,
    setUser,
    currentProject,
    setCurrentProject: setCurrentProjectAndClearCache
  }, !backendSearch && {
    UNSAFE_projects: projects,
    UNSAFE_addProject: addProject,
    UNSAFE_updateProject: updateProject,
    UNSAFE_removeProjectById: removeProjectById
  }), [loading, error, retry, projects, user, currentProject, backendSearch, addProject, updateProject, removeProjectById, setCurrentProjectAndClearCache]);
  return /*#__PURE__*/React.createElement(ProjectEntitlementsContext.Provider, {
    value: value
  }, children);
};
ProjectEntitlementsProvider.displayName = 'ProjectEntitlementsProvider';
ProjectEntitlementsProvider.propTypes = {
  /** Whether to disable all projects fetch when page loads */
  disableAllProjectsFetch: PropTypes.bool,
  /** Nodes with access to context */
  children: PropTypes.node
};
//# sourceMappingURL=ProjectEntitlementsContext.js.map